import { useState, useEffect } from 'react';

export default function CompromissoSubmodalidades() {
  const [nome, setNome] = useState('');
  const [periodo, setPeriodo] = useState(21);
  const [assinado, setAssinado] = useState(false);
  const [diasPraticados, setDiasPraticados] = useState<number[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem('compromisso-submodalidades');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setNome(data.nome || '');
        setPeriodo(data.periodo || 21);
        setAssinado(data.assinado || false);
        setDiasPraticados(data.diasPraticados || []);
      } catch (e) {
        console.error('Erro ao carregar compromisso', e);
      }
    }
  }, []);

  const salvarCompromisso = () => {
    if (nome.trim()) {
      const data = {
        nome,
        periodo,
        assinado: true,
        diasPraticados,
        dataInicio: new Date().toISOString()
      };
      localStorage.setItem('compromisso-submodalidades', JSON.stringify(data));
      setAssinado(true);
    }
  };

  const toggleDia = (dia: number) => {
    const newDias = diasPraticados.includes(dia)
      ? diasPraticados.filter(d => d !== dia)
      : [...diasPraticados, dia];
    setDiasPraticados(newDias);
    
    const saved = localStorage.getItem('compromisso-submodalidades');
    if (saved) {
      const data = JSON.parse(saved);
      data.diasPraticados = newDias;
      localStorage.setItem('compromisso-submodalidades', JSON.stringify(data));
    }
  };

  const calcularSequencia = () => {
    if (diasPraticados.length === 0) return 0;
    const sorted = [...diasPraticados].sort((a, b) => a - b);
    let maxSequencia = 1;
    let sequenciaAtual = 1;
    
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === sorted[i - 1] + 1) {
        sequenciaAtual++;
        maxSequencia = Math.max(maxSequencia, sequenciaAtual);
      } else {
        sequenciaAtual = 1;
      }
    }
    return maxSequencia;
  };

  const progresso = Math.round((diasPraticados.length / periodo) * 100);
  const sequencia = calcularSequencia();

  return (
    <section className="py-16 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Compromisso de Prática
          </h2>
          <p className="text-lg text-gray-600">
            Assine seu compromisso e acompanhe sua jornada de transformação
          </p>
        </div>

        {!assinado ? (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Seu Nome Completo
                </label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Digite seu nome"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Período de Prática
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  {[7, 14, 21, 30, 60, 90].map((dias) => (
                    <button
                      key={dias}
                      onClick={() => setPeriodo(dias)}
                      className={`px-4 py-3 rounded-xl font-semibold transition-all whitespace-nowrap ${
                        periodo === dias
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg scale-105'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {dias} dias
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-xl border-2 border-indigo-200">
                <p className="text-gray-800 leading-relaxed">
                  Eu, <strong className="text-indigo-600">{nome || '_______________'}</strong>, comprometo-me a praticar a técnica de Submodalidades por <strong className="text-indigo-600">{periodo} dias consecutivos</strong>, dedicando tempo para mapear e transformar minhas experiências internas, ajustando conscientemente as qualidades visuais, auditivas e cinestésicas das minhas representações mentais.
                </p>
              </div>

              <button
                onClick={salvarCompromisso}
                disabled={!nome.trim()}
                className="w-full px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-quill-pen-line text-xl"></i>
                Assinar Compromisso
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Seu Progresso</h3>
                <span className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full text-sm font-semibold">
                  {periodo} dias
                </span>
              </div>

              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <i className="ri-calendar-check-line text-white text-2xl"></i>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{diasPraticados.length}</p>
                  <p className="text-sm text-gray-600">Dias Praticados</p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <i className="ri-fire-line text-white text-2xl"></i>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{sequencia}</p>
                  <p className="text-sm text-gray-600">Maior Sequência</p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <i className="ri-percent-line text-white text-2xl"></i>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{progresso}%</p>
                  <p className="text-sm text-gray-600">Progresso</p>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700">Progresso Total</span>
                  <span className="text-sm font-bold text-indigo-600">{progresso}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full transition-all duration-500"
                    style={{ width: `${progresso}%` }}
                  ></div>
                </div>
              </div>

              {sequencia >= 7 && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-4 rounded-lg mb-4">
                  <div className="flex items-center gap-3">
                    <i className="ri-trophy-line text-green-600 text-2xl"></i>
                    <p className="text-sm text-green-900 font-semibold">
                      Incrível! Você manteve uma sequência de {sequencia} dias! Continue assim! 🔥
                    </p>
                  </div>
                </div>
              )}

              {progresso === 100 && (
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500 p-4 rounded-lg mb-4">
                  <div className="flex items-center gap-3">
                    <i className="ri-medal-line text-yellow-600 text-2xl"></i>
                    <p className="text-sm text-yellow-900 font-semibold">
                      Parabéns! Você completou seu objetivo de {periodo} dias! 🎉
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-7 gap-2">
                {Array.from({ length: periodo }, (_, i) => i + 1).map((dia) => (
                  <button
                    key={dia}
                    onClick={() => toggleDia(dia)}
                    className={`aspect-square rounded-lg font-semibold text-sm transition-all whitespace-nowrap flex items-center justify-center ${
                      diasPraticados.includes(dia)
                        ? 'bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-md scale-105'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {dia}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}